<!-- Waiting for the user's reply: the one thing that needs action -->
@if (waitingTickets().length > 0) {
  <div class="sp-notice" role="status">
    <p class="sp-notice__text">
      @if (waitingTickets().length === 1) {
        Te escribimos sobre <strong>{{ code(waitingTickets()[0].ticketNumber) }} {{ waitingTickets()[0].subject }}</strong>. Necesitamos tu respuesta para seguir.
      } @else {
        Tienes <strong>{{ waitingTickets().length }} tickets</strong> esperando tu respuesta.
      }
    </p>
    <button type="button" class="sp-btn sp-btn--ghost" (click)="showFirstWaiting()">
      {{ waitingTickets().length === 1 ? 'Ver ticket' : 'Ver el primero' }}
    </button>
  </div>
}

<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <div class="sp-tabs" role="group" aria-label="Filtrar tickets">
    <button type="button" class="sp-tab" [attr.aria-pressed]="filter() === 'active'" (click)="filter.set('active')">
      En curso <span class="sp-tab__count">{{ activeTickets().length }}</span>
    </button>
    <button type="button" class="sp-tab" [attr.aria-pressed]="filter() === 'finished'" (click)="filter.set('finished')">
      Cerrados <span class="sp-tab__count">{{ finishedTickets().length }}</span>
    </button>
  </div>
  <button type="button" class="sp-btn sp-btn--primary inline-flex shrink-0 items-center justify-center gap-2" (click)="openCreate()">
    <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
    </svg>
    Reportar un problema
  </button>
</div>

<!-- Loading -->
@if (!loaded()) {
  <ul class="sp-list" role="status" aria-label="Cargando tickets">
    @for (i of [1, 2, 3]; track i) {
      <li class="sp-row sp-row--skeleton animate-pulse">
        <span class="sp-row__main">
          <span class="h-3 w-24 rounded bg-gray-200 dark:bg-gray-800"></span>
          <span class="h-4 w-64 max-w-full rounded bg-gray-200 dark:bg-gray-800"></span>
        </span>
        <span class="h-2 w-40 rounded bg-gray-100 dark:bg-gray-800/70"></span>
      </li>
    }
  </ul>
}

<!-- Empty -->
@else if (visibleTickets().length === 0) {
  <div class="sp-empty">
    <svg class="sp-empty__icon" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
    <h3 class="sp-empty__title">{{ emptyState().title }}</h3>
    <p class="sp-empty__text">{{ emptyState().text }}</p>
    @if (emptyState().action) {
      <button type="button" class="sp-btn sp-btn--primary" (click)="openCreate()">Reportar un problema</button>
    }
  </div>
}

<!-- Tickets -->
@else {
  <ul class="sp-list" [class.opacity-60]="loading()">
    @for (ticket of visibleTickets(); track ticket.supportTicketId) {
      <li>
        <button type="button" class="sp-row sp-row--action" [attr.data-status]="ticket.status"
          (click)="openDetail(ticket)" [attr.aria-label]="rowLabel(ticket)">
          <span class="sp-row__main">
            <span class="sp-row__meta">
              <span class="sp-code">{{ code(ticket.ticketNumber) }}</span>
              <span>{{ categoryLabel(ticket.category) }}</span>
              @if (ticket.impact === 'high' && !isFinished(ticket)) {
                <span class="sp-flag">No puedo trabajar</span>
              }
              @if (ticket.messageCount > 0) {
                <span class="sp-msgcount">{{ ticket.messageCount }} {{ ticket.messageCount === 1 ? 'mensaje' : 'mensajes' }}</span>
              }
            </span>
            <span class="sp-row__subject">{{ ticket.subject }}</span>
            <span class="sp-row__time">{{ lastActivity(ticket) }}</span>
          </span>

          <span class="sp-rail" [attr.data-status]="ticket.status" aria-hidden="true">
            @if (ticket.status === 'closed') {
              <span class="sp-rail__closed">Cerrado por ti</span>
            } @else {
              <span class="sp-rail__track">
                @for (step of steps; track step.status; let i = $index) {
                  <span class="sp-rail__step" [attr.data-state]="stepState(ticket, i)"></span>
                }
              </span>
              <span class="sp-rail__label">{{ statusLabel(ticket.status) }}</span>
            }
          </span>
        </button>
      </li>
    }
  </ul>
}

<p class="sp-fallback">
  ¿Prefieres el correo? Escríbenos a
  <a [href]="'mailto:' + contactEmail">{{ contactEmail }}</a>
</p>

<!-- Detail drawer -->
@if (selected(); as ticket) {
  <div class="sp-backdrop" (click)="closeDetail()" aria-hidden="true"></div>
  <div class="sp-panel" role="dialog" aria-modal="true" aria-labelledby="sp-detail-title">
    <div class="sp-panel__head">
      <div class="min-w-0">
        <p class="sp-panel__code">
          <span class="sp-code">{{ code(ticket.ticketNumber) }}</span>
          <span class="sp-status" [attr.data-status]="ticket.status">{{ statusLabel(ticket.status) }}</span>
        </p>
        <h2 id="sp-detail-title" class="sp-panel__title">{{ ticket.subject }}</h2>
      </div>
      <button #detailCloseBtn type="button" class="sp-icon-btn" (click)="closeDetail()" aria-label="Cerrar detalle">
        <svg class="size-5" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="sp-panel__body">
      <!-- Lifecycle -->
      <section aria-labelledby="sp-progress-title">
        <h3 id="sp-progress-title" class="sp-subtitle">Seguimiento</h3>
        @if (ticket.status === 'closed') {
          <p class="sp-closed-note">
            Cerraste este ticket el {{ ticket.closedAt | date: "d 'de' MMMM, h:mm a" }}. Si el problema volvió, puedes reabrirlo.
          </p>
        } @else {
          <ol class="sp-timeline">
            @for (step of steps; track step.status; let i = $index) {
              @let state = stepState(ticket, i);
              @let when = stepDate(ticket, i);
              <li class="sp-timeline__item" [attr.data-state]="state" [attr.data-status]="step.status"
                [attr.aria-current]="state === 'current' ? 'step' : null">
                <span class="sp-timeline__dot" aria-hidden="true"></span>
                <span class="sp-timeline__label">{{ step.label }}</span>
                @if (when && state !== 'todo') {
                  <span class="sp-timeline__date">{{ when | date: "d MMM, h:mm a" }}</span>
                }
                @if (state === 'current' && step.status === 'open') {
                  <span class="sp-timeline__note">Lo vemos pronto. Te avisamos por correo cuando empecemos.</span>
                }
                @if (state === 'current' && step.status === 'in_progress') {
                  <span class="sp-timeline__note">Alguien del equipo está trabajando en tu caso.</span>
                }
                @if (state === 'current' && step.status === 'waiting_user') {
                  <span class="sp-timeline__note">Te escribimos en la conversación de abajo. Respóndenos para seguir con tu caso.</span>
                }
              </li>
            }
          </ol>
        }
      </section>

      <section aria-labelledby="sp-desc-title">
        <h3 id="sp-desc-title" class="sp-subtitle">Lo que nos contaste</h3>
        <p class="sp-description">{{ ticket.description }}</p>
      </section>

      <section class="sp-convo" aria-labelledby="sp-convo-title">
        <h3 id="sp-convo-title" class="sp-subtitle">Conversación</h3>
        <app-support-thread [messages]="messages()" [loading]="messagesLoading()" viewer="user"
          emptyText="Cuando el equipo te escriba, lo verás aquí y te avisaremos por correo." />

        @if (isFinished(ticket)) {
          <p class="sp-closed-note mt-3">Este ticket está {{ ticket.status === 'resolved' ? 'resuelto' : 'cerrado' }}. Reábrelo si necesitas escribirnos de nuevo.</p>
        } @else {
          <form class="sp-reply" (ngSubmit)="sendReply(ticket)">
            <label for="sp-reply-box" class="sr-only">Escribe un mensaje al equipo de soporte</label>
            <textarea #replyBox id="sp-reply-box" name="reply" rows="3" class="sp-reply__input"
              [ngModel]="replyText()" (ngModelChange)="replyText.set($event)" [maxlength]="replyMax"
              [placeholder]="ticket.status === 'waiting_user' ? 'Responde lo que te pedimos…' : 'Agrega un detalle o una captura de lo que pasa…'"></textarea>
            <div class="sp-reply__bar">
              <span class="sp-reply__hint">Te avisamos por correo cuando respondamos.</span>
              <button type="submit" class="sp-btn sp-btn--primary" [disabled]="!replyText().trim() || sendingReply()">
                {{ sendingReply() ? 'Enviando…' : 'Enviar mensaje' }}
              </button>
            </div>
          </form>
        }
      </section>

      <dl class="sp-facts">
        <div>
          <dt>Área</dt>
          <dd>{{ categoryLabel(ticket.category) }}</dd>
        </div>
        <div>
          <dt>Impacto</dt>
          <dd><span class="sp-impact" [attr.data-impact]="ticket.impact">{{ impactLabel(ticket.impact) }}</span></dd>
        </div>
        <div>
          <dt>Enviado</dt>
          <dd>{{ ticket.createdAt | date: "d 'de' MMMM y, h:mm a" }}</dd>
        </div>
        @if (ticket.contactPhone) {
          <div>
            <dt>Teléfono</dt>
            <dd>{{ ticket.contactPhone }}</dd>
          </div>
        }
      </dl>

      @if (ticket.status === 'in_progress') {
        <p class="sp-lock-note">Mientras lo revisamos no se puede editar el ticket. Si necesitas agregar algo, escríbelo en la conversación.</p>
      }
    </div>

    <div class="sp-panel__foot">
      <div class="flex items-center gap-1">
        @if (ticket.status === 'open') {
          <button type="button" class="sp-btn sp-btn--danger-text" (click)="confirmDeleteOpen.set(true)">Eliminar</button>
        }
        @if (!isFinished(ticket)) {
          <button type="button" class="sp-btn sp-btn--quiet" (click)="confirmCloseOpen.set(true)">Cerrar ticket</button>
        }
      </div>

      @if (isFinished(ticket)) {
        <button type="button" class="sp-btn sp-btn--primary" (click)="reopen(ticket)">Reabrir ticket</button>
      } @else if (ticket.status === 'waiting_user') {
        <div class="flex items-center gap-1">
          <button type="button" class="sp-btn sp-btn--quiet" (click)="openEdit(ticket)">Editar</button>
          <button type="button" class="sp-btn sp-btn--primary" (click)="focusReply()">Responder</button>
        </div>
      } @else if (canEdit(ticket)) {
        <button type="button" class="sp-btn sp-btn--primary inline-flex items-center gap-2" (click)="openEdit(ticket)">
          <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
          </svg>
          Editar ticket
        </button>
      }
    </div>
  </div>
}

<!-- Create / edit -->
<app-modal [open]="modalOpen()" [title]="modalTitle()" [size]="'md-1'"
  [bodyComponent]="TicketForm" [bodyInputs]="modalBodyInputs" [closeOnBackdrop]="false"
  (confirm)="onModalConfirm($event)" (close)="closeModal()"
  [confirmButtonText]="modalConfirmText()" [isSaving]="saving()">
</app-modal>

<!-- Close confirmation -->
<app-confirm-modal [open]="confirmCloseOpen()" (openChange)="confirmCloseOpen.set($event)"
  title="Cerrar ticket"
  [subtitle]="'Vas a cerrar ' + (selected() ? code(selected()!.ticketNumber) : '') + '. Dejaremos de trabajar en él, pero podrás reabrirlo si lo necesitas.'"
  cancelText="No, volver" confirmText="Sí, cerrar" variant="warning"
  (confirm)="onConfirmClose()">
</app-confirm-modal>

<!-- Delete confirmation -->
<app-confirm-modal [open]="confirmDeleteOpen()" (openChange)="confirmDeleteOpen.set($event)"
  title="Eliminar ticket"
  [subtitle]="'Vas a eliminar ' + (selected() ? code(selected()!.ticketNumber) : '') + '. Aún no lo hemos revisado, así que no quedará registro del caso.'"
  cancelText="No, volver" confirmText="Sí, eliminar" variant="danger"
  (confirm)="onConfirmDelete()">
</app-confirm-modal>
